import React, {useState, useEffect} from 'react'
import {connect} from 'react-redux';
import {startPoll, stopPoll, POLL_STATE} from 'redux/actions/Polling';
import config from 'lib/config';

import {Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment} from '@material-ui/core'

import Alert from '@material-ui/lab/Alert'

import {makeStyles} from '@material-ui/core/styles'

import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import AddCircleIcon from '@material-ui/icons/AddCircle'
import RemoveRedEye from '@material-ui/icons/RemoveRedEye'
import VisibilityOff from '@material-ui/icons/VisibilityOff'

import {cloudModuleConfigFieldLabels, cloudModuleMetadata, INTEGRATION_STATUS} from './constants'
import PropTypes from "prop-types";

import SimpleTable from 'views/Components/SimpleTable'

import {Integration as IntegrationModel} from 'lib/models/Integration'
import moment from "moment-timezone";
import {loadPageData} from "../../../redux/actions/Request";
import FormControl from "@material-ui/core/FormControl";
import Divider from "@material-ui/core/Divider";
import TextField from "@material-ui/core/TextField";
import { IntegrationLogo } from './IntegrationLogo';

const useStyles = makeStyles((theme) => ({
  moduleOptionsContainer: {
    display: 'flex',
    paddingBottom: 20
  },
  moduleButton: {
    border: `1px solid ${theme.palette.divider}`,
    margin: 5,
    padding: 15,
  },
  moduleOptionContainer: {
    width: 250,
    display: 'flex',
    cursor: 'pointer',
    flexDirection: 'column',
    justifyContent: 'space-between',
    borderRadius: theme.shape.borderRadius,
    textAlign: 'left',
  },
  textContainer: {
    display: 'flex',
    flexWrap: 'no-wrap',
    flexDirection: 'column'
  },
  moduleTitleText: {
    margin: 0,
  },
  moduleSubtitleText: {
    margin: 0,
    fontWeight: 'bold'
  },
  form: {
    width: 600,
    paddingTop: 10,
    '& fieldset': {
      padding: `0 !important`
    }
  },
  dialogActions: {
    padding: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  dialogActionsBtnContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  alert: {
    margin: '10px 0',
    backgroundColor: '#D9EBFF'
  },
  error: {
    color: 'red'
  }
}))

const formStyles = makeStyles((theme) => ({
  formControl: {
    padding: theme.spacing(3),
  },
  button: {
    margin: theme.spacing(0, 0, 0, 1),
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
  radio: {
    marginBottom: theme.spacing(0),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  secretsTitle: {
    fontWeight: 'bold',
  },
  secretsText: {
    fontSize: '.85rem',
    marginBottom: theme.spacing(1),
  },
}))

const CloudModuleConfigurationForm = (props) => {
  const ConfigSection = 'configuration'
  const SecretSection = 'secret'
  const RootSection = ''

  const classes = formStyles()
  const { model, disabled, validateAll, onSubmit, schemas, superadmin } = props

  const [nameValue, setNameValue] = useState(model.integrationName || '')
  const [configValues, setConfigValues] = useState(model.configuration || {})
  const [secretValues, setSecretValues] = useState({})
  const [secretIsMasked, setSecretIsMasked] = useState(true)
  const [maskedInputIndexes, setMaskedInputIndexes] = useState([0, 1, 2, 3, 4]) // default secrets to hidden

  const [validationErrors, setValidationErrors] = useState({})

  useEffect(() => {
    const hasValidationError = Object.values(validationErrors).some(value => value === true)
    props.onValidation({ hasValidationError })
  }, [validationErrors])

  const integrationType = model.integrationType
  if (!integrationType || integrationType === '') {
    console.error('integration has no type')
    return <div />
  }
  const integrationSchema = schemas.find((schema) => schema.integrationType === integrationType)
  if (!integrationSchema) {
    console.error('no schema for this integration type was returned from the server', integrationType)
    return <div />
  }
  const configurationSchema = integrationSchema.configurationSchema
  const secretSchema = integrationSchema.secretSchema

  const areSecretValuesEmpty = () => {
    let areSecretValuesEmpty = true
    for (const [key, value] of Object.entries(secretValues)) { /* eslint-disable-line */
      if (value && value !== '') {
        areSecretValuesEmpty = false
        break
      }
    }
    return areSecretValuesEmpty
  }

  const nameFieldInfo = {
    section: RootSection,
    label: 'Cloud Connector Name',
    name: 'integrationName',
    editable: true,
    required: true,
    placeholder: '',
  }

  let configFields = []
  for (const [prop, propInfo] of Object.entries(configurationSchema['properties'] || {})) {
    if (propInfo.type !== 'string') {
      console.warn('unimplemented field type', propInfo.type)
      continue
    }
    const requiredBySchema = configurationSchema['required'] && configurationSchema['required'].includes(prop)
    if(cloudModuleConfigFieldLabels[integrationType][ConfigSection] && cloudModuleConfigFieldLabels[integrationType][ConfigSection][prop]){
      configFields = configFields.concat({
        section: ConfigSection,
        label: cloudModuleConfigFieldLabels[integrationType][ConfigSection][prop],
        name: prop,
        editable: true,
        required: requiredBySchema,
        placeholder: requiredBySchema ? '(required)' : '(optional)'
      })
    }
  }

  let secretFields = []
  for (const [prop, propInfo] of Object.entries(secretSchema['properties'])) {
    if (propInfo.type !== 'string') {
      console.warn('unimplemented field type', propInfo.type)
      continue
    }
    const requiredBySchema = secretSchema['required'] && secretSchema['required'].includes(prop)
    const fieldCurrentlyRequired = !model.id ? requiredBySchema : (areSecretValuesEmpty() ? false : requiredBySchema)
    secretFields = secretFields.concat({
      section: SecretSection,
      label: cloudModuleConfigFieldLabels[integrationType][SecretSection][prop],
      name: prop,
      editable: true,
      required: fieldCurrentlyRequired,
      placeholder: fieldCurrentlyRequired ? '(required)' : (!model.id ? '(optional)' : '(no change)')
    })
  }

  const isValidationErrorsUninitialized = Object.keys(validationErrors).length === 0;
  if (isValidationErrorsUninitialized) {
    let newValidationErrors = {};

    [...configFields, ...secretFields].forEach((fieldInfo) => {
      const value = ""

      newValidationErrors = {
        ...newValidationErrors,
        [fieldInfo.section + '.' + fieldInfo.name]: fieldInfo.validation ? !fieldInfo.validation.test(value) : fieldInfo.required && value === '',
      }
    })

    setValidationErrors(newValidationErrors)
  }

  const updateValidation = (fieldInfo, value) => {
    setValidationErrors({
      ...validationErrors,
      [fieldInfo.section + '.' + fieldInfo.name]: fieldInfo.validation ? !fieldInfo.validation.test(value) : fieldInfo.required && value === '',
    })
  }

  const handleChange = (fieldInfo, value) => {

    updateValidation(fieldInfo, value)

    if (fieldInfo.section === ConfigSection) {
      setConfigValues({
        ...configValues,
        [fieldInfo.name]: value,
      })
    } else if (fieldInfo.section === SecretSection) {
      setSecretValues({
        ...secretValues,
        [fieldInfo.name]: value,
      })
    } else if (fieldInfo.section === RootSection && fieldInfo.name === nameFieldInfo.name) {
      setNameValue(value)
    } else {
      console.error('unexpected section', fieldInfo)
    }
  }

  const handleBlur = (fieldInfo, value) => {
    updateValidation(fieldInfo, value)

    model.set({[nameFieldInfo.name]: nameValue}, false)
    model.set({configuration: configValues}, false)

    if (areSecretValuesEmpty()) {
      model.set({secret: undefined}, false)
    } else {
      model.set({secret: secretValues}, false)
    }
  }

  const toggleMask = inputIndex => {
    const foundInputIndex = maskedInputIndexes.find(i => i === inputIndex)
    const newMaskedInputIndexes = foundInputIndex > -1 ? [...maskedInputIndexes].filter(i => i !== foundInputIndex) : [...maskedInputIndexes].concat([inputIndex])
    setMaskedInputIndexes(newMaskedInputIndexes)
    setSecretIsMasked(!secretIsMasked)
  }

  const isInputMasked = inputIndex => {
    return maskedInputIndexes.find(i => i === inputIndex) > -1
  }

  const isStringLengthValid = (string, maxLength)=> string.length <= maxLength;
  const isStringCharactersValid = (string, regex) => regex.test(string);

  return (
    <form onSubmit={(e) => {
      e.preventDefault()
      onSubmit()
    }}>
      <FormControl component="fieldset" fullWidth={true} className={classes.formControl}>
        <TextField
          autoFocus={true}
          value={nameValue}
          label={nameFieldInfo.label}
          datacy={nameFieldInfo.name}
          onChange={(e) => handleChange(nameFieldInfo, e.target.value)}
          onBlur={(e) => handleBlur(nameFieldInfo, e.target.value)}
          className={classes.field}
          disabled={disabled || !nameFieldInfo.editable}
          key={RootSection+nameFieldInfo.name}
          error={!!validationErrors[RootSection+'.'+nameFieldInfo.name] || !isStringLengthValid(nameValue, 255) || !isStringCharactersValid(nameValue, /^[A-Za-z0-9 -]+$/)}
          helperText={!isStringLengthValid(nameValue, 255) || !isStringCharactersValid(nameValue, /^[A-Za-z0-9 -]+$/) ? 'must be less than 256 characters and only contain letters, numbers, or dashes' : ''}
        />


        {
          configFields.map((fieldInfo, index) => (
            <TextField
              datacy={fieldInfo.name}
              value={configValues[fieldInfo.name] || ''}
              placeholder={fieldInfo.placeholder}
              label={fieldInfo.label}
              onChange={(e) => handleChange(fieldInfo, e.target.value)}
              onBlur={(e) => handleBlur(fieldInfo, e.target.value)}
              className={classes.field}
              disabled={disabled || !fieldInfo.editable}
              key={ConfigSection+index}
              error={!!validationErrors[ConfigSection+'.'+fieldInfo.name] || (validateAll && fieldInfo.required && (!configValues[fieldInfo.name] || configValues[fieldInfo.name] === ''))}
              helperText={fieldInfo.helperText}
            />
          ))
        }

        <Divider className={classes.divider} />

        <p className={classes.secretsText}><span className={classes.secretsTitle}>Secrets: </span>
          { model.id ? 'Leave the following fields blank to leave this connector’s secrets unchanged.' : 'The following entries will be hidden when editing this cloud connector in the future.'}
        </p>

        {
          secretFields.map((fieldInfo, index) => (
            <TextField
              inputProps={isInputMasked(index) ? {
                style: {
                  fontFamily: 'text-security-disc'
                }
              } : {}}
              InputProps={{
                endAdornment: (
                  <InputAdornment style={{ cursor: 'pointer' }} position={'end'} onClick={() => toggleMask(index)}>
                    {isInputMasked(index) ? <RemoveRedEye /> : <VisibilityOff />}
                  </InputAdornment>
                ),
              }}
              label={fieldInfo.label}
              datacy={fieldInfo.name}
              key={SecretSection+index}
              className={classes.field}
              placeholder={fieldInfo.placeholder}
              value={secretValues[fieldInfo.name] || ''}
              disabled={disabled || !fieldInfo.editable}
              onBlur={(e) => handleBlur(fieldInfo, e.target.value)}
              onChange={(e) => handleChange(fieldInfo, e.target.value)}
              error={!!validationErrors[SecretSection+'.'+fieldInfo.name] || (validateAll && fieldInfo.required && (!secretValues[fieldInfo.name] || secretValues[fieldInfo.name] === ''))}
            />

          ))
        }


        { model.id && superadmin &&
          <div>
            <Divider className={classes.divider} />
            <p className={classes.secretsText}><span className={classes.secretsTitle}>Admin Info: </span>
              This information is only visible to Blumira superadmins.
            </p>
            <p className={classes.secretsText}><span className={classes.secretsTitle}>Connector ID: </span>
              {model.id}
            </p>
          </div>
        }

      </FormControl>
    </form>
  )
}


const renderLogoCell = (column, model) => {
  return <IntegrationLogo integrationType={model[column.field]} width="65" height="30" />
}

const useStatusStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center'
  },
  circle: {
    minWidth: 15,
    height: 15,
    marginRight: 10,
    borderRadius: '100%'
  },
  progress: {
    marginRight: 10,
  }
}))

const IntegrationStatus = (props) => {
  const classes = useStatusStyles()
  if (props.status === INTEGRATION_STATUS.INIT) {
    return (
      <span className={classes.container}>
        <CircularProgress size={20} className={classes.progress} />
        <span>Initializing…</span>
      </span>
    )
  } else if (props.status === INTEGRATION_STATUS.OK) {
    return (
      <span className={classes.container}>
        <div className={classes.circle} style={{backgroundColor: '#3BB273'}} />
        Online
      </span>
    )
  } else if (props.status === INTEGRATION_STATUS.ERROR) {
    return (
      <span className={classes.container}>
        <div className={classes.circle} style={{backgroundColor: '#E76F51'}} />
        {props.statusMessage
          ? <span>Error: <tt>{props.statusMessage}</tt></span>
          : <span>Error</span>
        }
      </span>
    )
  } else {
    return <span>{props.status}</span>
  }
}

const renderStatusCell = (column, model) => {
  return <IntegrationStatus status={model[column.field]} statusMessage={model['integrationStatusMessage']}/>;
}

const renderDateCell = (column, model) => {
  const timeValue = model[column.field]
  if (!timeValue) {
    return 'Pending'
  }
  return moment.utc(timeValue).tz(moment.tz.guess(true)).format('lll z');
}

const renderModifiedCell = (column, model) => {
  var timeValue = model[column.field]
  if (!timeValue) {
    timeValue = model['created']
  }
  return moment.utc(timeValue).tz(moment.tz.guess(true)).format('lll z');
}



const CloudModules = (props) => {
  const classes = useStyles()

  const {
    integrations, orgColumnStats, ready, orgId
  } = props;

  const renderLogCountCell = (column, model) => {
    const thisOrgStats = orgColumnStats.find(stats => stats.orgId === orgId) || {};
    const runningHistory = thisOrgStats.running_history || {};
    const runningRecent = thisOrgStats.running_recent || {};
    const historyLogs = (runningHistory[model.id] || [0])[0];
    const recentLogs = (runningRecent[model.id] || [0])[0];
    var logs = historyLogs + recentLogs;
    let n = 0;
    while (logs > 1000) {
      n += 1;
      logs = Math.floor(logs / 1000)
    }

    if (n === 0) {
      return logs
    }

    if (n === 1) {
      return `${logs}K`
    }

    if (n === 2) {
      return `${logs}M`
    }

    if (n === 3) {
      return `${logs}B`
    }

    if (n === 4) {
      return `${logs}T`
    }
  }

  const cloudModulesColumns = [
    {
      field: 'integrationType',
      renderValue: renderLogoCell,
      searchable: false,
    },
    {
      title: 'Cloud Connector Name',
      field: 'integrationName',
      searchable: true,
    },
    {
      title: 'Current Status',
      field: 'integrationStatus',
      searchable: true,
      renderValue: renderStatusCell,
    },
    {
      title: 'Total Logs',
      field: 'totalLogs',
      searchable: false,
      renderValue: renderLogCountCell,
    },
    {
      title: 'Last Status Update',
      field: 'lastStatusUpdate',
      searchable: false,
      renderValue: renderDateCell,
    },
    {
      title: 'Created',
      field: 'created',
      searchable: false,
      renderValue: renderDateCell,
    },
    {
      title: 'Last Modified',
      field: 'modified',
      searchable: false,
      renderValue: renderModifiedCell,
    },
  ]


  const [currentStep, setCurrentStep] = useState(0)
  const [selectedModel, setSelectedModel] = useState(null)
  const [isAlertVisible, setIsAlertVisible] = useState(true)
  const [isErrorDisplayed, setIsErrorDisplayed] = useState(false)
  const [isModuleDialogOpen, setIsModuleDialogOpen] = useState(false)
  const [selectedCloudModule, setSelectedCloudModule] = useState(null)
  const [isConnectingOrSaving, setIsConnectingOrSaving] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [pendingRemoveSelectedModule, setPendingRemoveSelectedModule] = useState(false)
  const [hasValidationError, setHasValidationError] = useState(false)

  useEffect(() => {
    // for each integration
    props.integrations.map(integration => {
      if(integration.integrationStatus === INTEGRATION_STATUS.INIT || integration.lastStatusUpdate === null){
        if(props.polls[integration.id] && props.polls[integration.id].state === POLL_STATE.STOPPED){
          // if the integration status is initialize and the poll is already registered, but stopped
          // start the poll
          return props.startPoll(integration, 5)
        } else if(!props.polls[integration.id]) {
          // if the integration status is initialize and there is no poll registered
          // start the poll
          return props.startPoll(integration, 5)
        }
        return null
      }else{
        if(props.polls[integration.id] && props.polls[integration.id].state === POLL_STATE.RUNNING){
          // if the integration status isn't initialize but there is a running poll
          // stop the poll
          return props.stopPoll([integration.id])
        }
        return null
      }
    })
  }, [props.integrations, props.polls]) /* eslint-disable-line */

  const toggleModal = () => {
    setIsErrorDisplayed(false)
    setIsConnectingOrSaving(false)
    setIsRemoving(false)
    setPendingRemoveSelectedModule(false)
    if (isModuleDialogOpen) {
      setTimeout(() => setCurrentStep(0), 200)
    }
    setIsModuleDialogOpen(!isModuleDialogOpen)
  }

  const handleNextStep = () => {
    let nextStep = currentStep + 1
    setCurrentStep(nextStep)
  }

  const handleSelectModule = module => {
    setSelectedCloudModule(module)
    setSelectedModel(new IntegrationModel({
      integrationName: `${module.title} Cloud Connector ${moment().format('MM/DD/YYYY').replace(/\//g, '-')}`,
      integrationType: module.integrationType,
      configuration: {},
      secret: {},
    }))
    handleNextStep()
  }

  const handleRowClick = model => {
    let module = cloudModuleMetadata.find(({integrationType}) => integrationType === model.integrationType)
    setSelectedCloudModule(module)
    setSelectedModel(model)
    setCurrentStep(1)
    toggleModal()
  }

  const handleRemoveButton = () => {
    setIsErrorDisplayed(false)
    setIsConnectingOrSaving(false)
    setIsRemoving(false)
    setPendingRemoveSelectedModule(true)
  }

  const handleConfirmPendingDelete = () => {
    setIsErrorDisplayed(false)
    setIsConnectingOrSaving(false)
    setIsRemoving(true)
    selectedModel.delete()
      .then(() => {
        props.reload(true)
        toggleModal()
      })
      .catch(e => {
        console.error(e)
        setIsErrorDisplayed(true)
        setIsConnectingOrSaving(false)
        setIsRemoving(false)
      })
  }

  const handleClearPendingDelete = () => {
    setIsErrorDisplayed(false)
    setIsConnectingOrSaving(false)
    setIsRemoving(false)
    setPendingRemoveSelectedModule(false)
  }

  const handleConnect = () => {
    const isUpdating = !!selectedModel.id
    setIsErrorDisplayed(false)
    setIsConnectingOrSaving(true)
    setIsRemoving(false)
    const currentSelectedModelId = isUpdating ? selectedModel.id : null
    const currentSelectedModelOrgId = isUpdating ? selectedModel.orgId : null
    const func = isUpdating ? 'update' : 'create'
    selectedModel[func]()
      .then(() => {
        props.reload(true)
        toggleModal()
      })
      .catch(e => {
        console.error(e)
        if(isUpdating){
          selectedModel.set({
            id: currentSelectedModelId,
            orgId: currentSelectedModelOrgId
          })
        }
        setIsErrorDisplayed(true)
        setIsConnectingOrSaving(false)
        setIsRemoving(false)
      })
  }

  const getConnectButtonText = () => {
    let textToReturn = isConnectingOrSaving ? 'Connecting...' : 'Connect'
    if (selectedModel && selectedModel.id)
      textToReturn = isConnectingOrSaving ? 'Saving...' : 'Save Changes'
    return textToReturn
  }

  const getRemoveButtonText = () => {
    return isRemoving ? 'Removing...' : 'Remove'
  }

  const currentDialogStepTitle = () => {
    return currentStep === 0 ? 'Available Cloud Connectors' : (selectedModel.id ? 'Edit Cloud Connector' : 'Add Cloud Connector')
  }

  const handleCancelModal = () => {
    if (selectedModel) {
      selectedModel.reset()
    }
    toggleModal()
  }

  const displayDialogActions = ({hasValidationError}) => {
    if (currentStep > 0 && pendingRemoveSelectedModule) {
      return <DialogActions className={classes.dialogActions}>
        <div className={classes.dialogActionsBtnContainer}>
          Are you sure you want to remove this cloud connector?<br/>
          <Button datacy={'cloudModuleConfirmRemoveBtn'} onClick={handleConfirmPendingDelete} disabled={!!isRemoving} className={classes.error}
                  variant={'text'} style={{marginLeft: 5}}>
            {isRemoving ? 'Removing...' : 'Confirm'}
          </Button>
          <Button onClick={handleClearPendingDelete} disabled={!!isRemoving} color={'primary'} variant={'contained'}
                  style={{marginLeft: 5}}>
            Cancel
          </Button>
        </div>
      </DialogActions>
    } else if (currentStep === 1) {
      return <DialogActions className={classes.dialogActions}>
        <div className={classes.dialogActionsBtnContainer}>
          {(selectedModel && selectedModel.id) &&
          <Button datacy={'cloudModuleRemoveBtn'} disabled={!!isRemoving} onClick={handleRemoveButton} className={classes.error} variant={'text'}
                  style={{marginLeft: 5}}>
            {getRemoveButtonText()}
          </Button>}
          <Button onClick={handleCancelModal} color={'primary'} variant={'text'} style={{marginLeft: 5}}>
            Cancel
          </Button>
          <Button datacy={'cloudModuleConnectBtn'} disabled={!!isConnectingOrSaving || hasValidationError} onClick={handleConnect} color={'primary'} variant={'contained'}
                  style={{marginLeft: 5}}>
            {getConnectButtonText()}
          </Button>
        </div>
        {selectedCloudModule && <a href={selectedCloudModule.setupGuideLink} target={'_blank'}
                                   rel="noopener noreferrer">{selectedCloudModule.title} setup
          guide <OpenInNewIcon/></a>}
      </DialogActions>
    }
    return null
  }

  const CloudButton = (props) => {
    return (
      <Button datacy={props.module.integrationType} onClick={() => handleSelectModule(props.module)} variant='outlined' className={classes.moduleButton} disabled={props.disabled}>
        <div className={classes.moduleOptionContainer}>
          {<IntegrationLogo integrationType={props.module.integrationType} width={props.module.imageHeight || 100} height={props.module.imageHeight ? props.module.imageHeight/2.166666667 : 46} />}
          <div className={classes.textContainer}>
            <p className={classes.moduleTitleText}>{props.module.title}</p>
            <p className={classes.moduleSubtitleText}>{props.module.subtitle}</p>
          </div>
        </div>
      </Button>
    )
  }

  const displayDialogContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className={classes.moduleOptionsContainer}>
            {
              cloudModuleMetadata.filter(
                module => config.cloud_connectors.includes(module.integrationType)
              ).map((module, idx) => {
                if((module.integrationType === 'mimecast/poll') && !props.isMimecastVisible){
                  return null
                }
                // Disable AWS connector on every license EXCEPT the ADVANCED license
                else if (module.integrationType === 'aws/Initialize' && (props.license.isTagged('limited-to-cloud') || props.license.isTagged('limited-to-microsoft'))) {
                  return <CloudButton key={module.integrationType} module={module} disabled={true} />
                // Disable Duo & SentinelOne connectors on FREE and M365 licenses
                } else if ((module.integrationType === 'sentinelone/poll' || module.integrationType === 'duo/poll') && props.license.isTagged('limited-to-microsoft')) {
                  return <CloudButton key={module.integrationType} module={module} disabled={true} />
                } else {
                  return <CloudButton key={module.integrationType} module={module} disabled={false} />
                }
              })
            }
          </div>
        )
      case 1:
        return (
          <div className={classes.moduleFormContainer}>
            {selectedCloudModule &&
            <IntegrationLogo integrationType={selectedCloudModule.integrationType} width={selectedCloudModule.imageHeight || 100} height={selectedCloudModule.imageHeight ? selectedCloudModule.imageHeight/2.166666667 : 46} />}
            {(isAlertVisible && selectedCloudModule && !pendingRemoveSelectedModule && !selectedModel.id) &&
            <Alert
              icon={false}
              className={classes.alert}
              onClose={() => setIsAlertVisible(false)}
            >
              Get started by <a target={'_blank'} rel="noopener noreferrer" href={selectedCloudModule.setupGuideLink}>opening
              the {selectedCloudModule.title} setup guide. <OpenInNewIcon/></a>
            </Alert>
            }
            {(isErrorDisplayed && selectedCloudModule) &&
            <p className={classes.error}>An error occurred trying to connect to {selectedCloudModule.title}.<br />
              Please ensure your entries are correct and try again.</p>}
            <div className={classes.form}>
              <CloudModuleConfigurationForm
                model={selectedModel}
                disabled={isConnectingOrSaving || isRemoving}
                onValidation={(validation) => setHasValidationError(validation.hasValidationError)}
                validateAll={isErrorDisplayed}
                onSubmit={handleConnect}
                schemas={props.integrationSchemas}
                superadmin={props.superadmin}
              />
            </div>
          </div>
        )
      default:
        return <span>error: unknown step {currentStep}</span>
    }
  }

  return (
    <div>
      <SimpleTable
        isNorthStar
        data={integrations}
        isFetching={!ready}
        rowClick={handleRowClick}
        columns={cloudModulesColumns}
        emptyText={'No connectors'}
        licenseRestriction={'integration.create'}
        toolbarActions={[
          {
            icon: AddCircleIcon,
            tooltip: 'Add Cloud Connector',
            onClick: toggleModal,
            disabled: props.license.isTagged('limited-to-microsoft') && integrations?.length === 1 ? true : false
          }
        ]}
      />
      <Dialog open={isModuleDialogOpen} onClose={handleCancelModal} maxWidth={'lg'}>
        <DialogTitle>{currentDialogStepTitle()}</DialogTitle>
        <DialogContent>{displayDialogContent()}</DialogContent>
        {displayDialogActions({hasValidationError})}
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state, ownProps) => {
  return {
    polls: state.request.polls,
    license: state.license,
    orgId: state.location.payload.orgId,
    superadmin: state?.session?.settings?.user?.superadmin,
    isMimecastVisible: state?.session?.settings?.user?.config?.mimecast_connector_beta
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    reload: (force) => dispatch(loadPageData(force)),
    startPoll: (model, interval, autoResume)=>dispatch(startPoll(model, interval, autoResume)),
    stopPoll: (modelIds)=>dispatch(stopPoll(modelIds)),
  }
}

CloudModules.propTypes = {
  integrations: PropTypes.array,
  integrationSchemas: PropTypes.array,
  orgColumnStats: PropTypes.array,
  ready: PropTypes.bool,
  superadmin: PropTypes.bool,
};

CloudModules.defaultProps = {
  integrations: [],
  integrationSchemas: [],
  orgColumnStats: [],
  ready: false,
  superadmin: false,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CloudModules);
